import "@deltasge/marauders-map/lib/utils/array.extensions";
import React, { FC, useCallback, useEffect, useState } from "react";
import {
  EUserType,
  IAluno,
  IAlunoLogin,
  ICliente,
  ICurso,
  IFiltroMateria,
  IFiltroSerie,
  IFiltroTurma,
  IMateria,
  IMatriculaAluno,
  IMensagemAtribuicao,
  IPeriodoEtapa,
  IProfessor,
  ISerie,
  ITurma,
  ITurmaGrupo,
  utils,
  yupMensagemAtribuicao,
} from "@deltasge/marauders-map";
import {
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { snack } from "components/GlobalSnackbar";
import { api } from "configs";
import { useAppSelector } from "store";
import { getError } from "utils";
import { useMobile } from "hooks/useMobile";

export const DialogFilterAtribuicao: FC<{
  onClose: () => void;
  addAtribuicao: (atribuicao: IMensagemAtribuicao, close?: boolean) => void;
  atribuicao?: IMensagemAtribuicao;
}> = ({ onClose, addAtribuicao, atribuicao: init }) => {
  const isSm = useMobile("sm");
  const {
    treeView: { cursos: todosOsCursos, professores: todosOsProfessores },
    anoLetivo: { anoLetivo },
    usuario: { usuario },
    sistemaAvaliacao: { data: sistemaAvaliacao },
    configuracoes: {
      etapas: todasEtapas,
      configOnline: { configuracoes },
    },
  } = useAppSelector((state) => state);

  const [atribuicao, setAtribuicao] = useState<IMensagemAtribuicao>({
    ...yupMensagemAtribuicao.getDefault(),
    ...init,
    anoLetivo: anoLetivo.toString(),
    aluno: init ? init.aluno : true,
    mensagemAtribuicaoAlunos: init ? init.mensagemAtribuicaoAlunos : [],
    mensagemAtribuicaoResponsaveis: init
      ? init.mensagemAtribuicaoResponsaveis
      : [],
    mensagemAtribuicaoLoginsExtras: init
      ? init.mensagemAtribuicaoLoginsExtras
      : [],
    mensagemAtribuicaoProfessores: init
      ? init.mensagemAtribuicaoProfessores
      : [],
  });

  const [matriculas, setMatriculas] = useState<IMatriculaAluno[]>([]);
  const [loading, setLoading] = useState(false);
  const [etapaRecuperacao, setEtapaRecuperacao] = useState<number>();
  const [optantes, setOptantes] = useState<number>();

  const cursos = utils.getCursosTreeView({
    cursos: todosOsCursos,
  });
  const curso = cursos.find((f) => f.id == atribuicao.idCurso);

  const series = utils.getSeriesTreeView({
    series: curso?.series,
  });
  const serie = series?.find((f) => f.id == atribuicao.idSerie);

  const turmas = utils.getTurmasTreeView({
    turmas: serie?.turmas,
  });
  const turma = turmas?.find((f) => f.id == atribuicao.idTurma);

  const materias = utils.getMateriasTreeView({
    serie,
    turma,
    sort: true,
  });
  const materia = materias.find((f) => f.id == atribuicao.idMateria);

  const professores = atribuicao.professor
    ? utils.getProfessoresTreeView({
        professores: todosOsProfessores,
        cursos,
        curso,
        serie,
        turma,
        materia,
      })
    : [];

  let etapas: IPeriodoEtapa[] | undefined = undefined;
  if (curso) {
    etapas = todasEtapas.filter((etapa) => {
      return (
        etapa.idsCursos?.includes(curso?.id ?? 0) &&
        etapa.etapa <= configuracoes.bim_site
      );
    });
    etapas.sort((a, b) => a.etapa - b.etapa);
  }

  const handleCheckAllARL = useCallback(
    (checked: boolean, matriculas: IMatriculaAluno[]) => {
      try {
        let {
          mensagemAtribuicaoAlunos: maa,
          mensagemAtribuicaoResponsaveis: mar,
          mensagemAtribuicaoLoginsExtras: male,
        } = atribuicao;

        maa = [];
        mar = [];
        male = [];

        if (atribuicao.aluno && checked) {
          maa.push(
            ...matriculas.map((m) => ({
              idAluno: m.aluno.uid,
              idMensagemAtribuicao: null,
              aluno: m.aluno as IAluno,
            }))
          );
        }
        if (atribuicao.responsavel && checked) {
          mar.push(
            ...matriculas.map((m) => {
              if (!m.responsavel?.id) {
                throw new Error("Responsável não encontrado");
              }
              return {
                idResponsavel: m.responsavel.id,
                idMensagemAtribuicao: null,
                responsavel: m.responsavel as ICliente,
              };
            })
          );
        }
        if (atribuicao.loginExtra && checked) {
          male.push(
            ...matriculas
              .map((m) => {
                return (m.loginExtras ?? []).map((loginExtra) => ({
                  idLoginExtra: loginExtra.id,
                  idMensagemAtribuicao: null,
                  loginExtra: loginExtra as IAlunoLogin,
                }));
              })
              .flatMap((f) => f)
          );
        }
        setAtribuicao((p) => ({
          ...p,
          mensagemAtribuicaoAlunos: maa,
          mensagemAtribuicaoResponsaveis: mar,
          mensagemAtribuicaoLoginsExtras: male,
        }));
      } catch (error) {
        snack.error(getError(error));
      }
    },
    [atribuicao]
  );

  const handleCheckAllProfessores = useCallback(
    (checked: boolean) => {
      try {
        if (checked) {
          setAtribuicao((p) => ({
            ...p,
            mensagemAtribuicaoProfessores: professores.map((f) => {
              if (!f.id) {
                throw new Error("Professor não encontrado");
              }
              return {
                idProfessor: f.id,
                idMensagemAtribuicao: null,
                professor: f as IProfessor,
              };
            }),
          }));
        } else {
          setAtribuicao((p) => ({
            ...p,
            mensagemAtribuicaoProfessores: [],
          }));
        }
      } catch (error) {
        snack.error(getError(error));
      }
    },
    [professores]
  );

  const handleChangeAtribuicaoARL = useCallback(
    (matAluno: IMatriculaAluno, checked: boolean) => {
      try {
        let {
          mensagemAtribuicaoAlunos: maa,
          mensagemAtribuicaoResponsaveis: mar,
          mensagemAtribuicaoLoginsExtras: male,
        } = atribuicao;

        if (!maa) {
          throw new Error("Atribuição de alunos não encontrada");
        }
        if (!mar) {
          throw new Error("Atribuição de responsáveis não encontrada");
        }
        if (!male) {
          throw new Error("Atribuição de logins extras não encontrada");
        }

        if (atribuicao.aluno) {
          if (checked) {
            maa = maa.filter((f) => f.idAluno != matAluno.aluno.uid);
          } else {
            maa.push({
              idAluno: matAluno.aluno.uid,
              idMensagemAtribuicao: null,
              aluno: matAluno.aluno as IAluno,
            });
          }
        }
        if (atribuicao.responsavel) {
          if (checked) {
            mar = mar.filter(
              (f) => f.idResponsavel != matAluno.responsavel?.id
            );
          } else {
            if (!matAluno.responsavel?.id) {
              throw new Error("Responsável não encontrado");
            }
            mar.push({
              idResponsavel: matAluno.responsavel.id,
              idMensagemAtribuicao: null,
              responsavel: matAluno.responsavel as ICliente,
            });
          }
        }
        if (atribuicao.loginExtra) {
          if (checked) {
            male = male.filter(
              (f) =>
                !(matAluno.loginExtras ?? [])
                  .map((l) => l.id)
                  .includes(f.idLoginExtra)
            );
          } else {
            male.push(
              ...(matAluno.loginExtras ?? []).map((l) => ({
                idLoginExtra: l.id,
                idMensagemAtribuicao: null,
                loginExtra: l as IAlunoLogin,
              }))
            );
          }
        }

        setAtribuicao((p) => ({
          ...p,
          mensagemAtribuicaoAlunos: maa,
          mensagemAtribuicaoResponsaveis: mar,
          mensagemAtribuicaoLoginsExtras: male,
        }));
      } catch (error) {
        snack.error(getError(error));
      }
    },
    [atribuicao, matriculas]
  );

  const handleChangeAtribuicaoProfessor = useCallback(
    (professor: Partial<IProfessor>, checked: boolean) => {
      try {
        let map = atribuicao.mensagemAtribuicaoProfessores;
        if (!map) {
          throw new Error("Atribuição de professores não encontrada");
        }
        if (checked) {
          map = map.filter((f) => f.idProfessor != professor.id);
        } else {
          if (!professor.id) {
            throw new Error("ID do professor não encontrado");
          }
          map.push({
            idProfessor: professor.id,
            idMensagemAtribuicao: null,
            professor: professor as IProfessor,
          });
        }
        setAtribuicao((p) => ({
          ...p,
          mensagemAtribuicaoProfessores: map,
        }));
      } catch (error) {
        snack.error(getError(error));
      }
    },
    [atribuicao]
  );

  const handleAdd = useCallback(
    (close?: boolean) => {
      if (
        atribuicao.aluno &&
        atribuicao.mensagemAtribuicaoAlunos &&
        atribuicao.mensagemAtribuicaoAlunos.length == matriculas.length &&
        !etapaRecuperacao
      ) {
        atribuicao.mensagemAtribuicaoAlunos = [];
      }

      if (
        atribuicao.responsavel &&
        atribuicao.mensagemAtribuicaoResponsaveis &&
        atribuicao.mensagemAtribuicaoResponsaveis.length == matriculas.length &&
        !etapaRecuperacao
      ) {
        atribuicao.mensagemAtribuicaoResponsaveis = [];
      }

      if (
        atribuicao.loginExtra &&
        atribuicao.mensagemAtribuicaoLoginsExtras &&
        atribuicao.mensagemAtribuicaoLoginsExtras.length ==
          matriculas
            .filter((f) => !!f.loginExtras)
            .map((m) => m.loginExtras)
            .flatMap((m) => m).length &&
        !etapaRecuperacao
      ) {
        atribuicao.mensagemAtribuicaoLoginsExtras = [];
      }

      addAtribuicao(atribuicao, close);
    },
    [addAtribuicao, atribuicao, matriculas]
  );

  const {
    mensagemAtribuicaoLoginsExtras,
    mensagemAtribuicaoAlunos,
    mensagemAtribuicaoProfessores,
    mensagemAtribuicaoResponsaveis,
    aluno,
    responsavel,
    loginExtra,
  } = atribuicao;

  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        if (turma?.id && (aluno || responsavel || loginExtra)) {
          if (ativo) setLoading(true);
          const { data } = await api.get<ITurmaGrupo[] | undefined>(
            "turma/alunos/por-turma",
            {
              params: {
                idsTurmas: [turma?.id],
                comLoginsExtra: atribuicao.loginExtra ? "true" : "false",
                comResponsaveis: atribuicao.responsavel ? "true" : "false",
                recuperacaoEtapa: etapaRecuperacao,
                recuperacaoMateria: atribuicao.idMateria,
                optanteEtapa: optantes,
                optanteMateria: optantes ? atribuicao.idMateria : undefined,
                situacaoMatricula: utils.enumMatriculasAtivas,
              },
            }
          );
          const destinatiarios = data?.at(0)?.destinatarios;
          if (destinatiarios && ativo) {
            setMatriculas(destinatiarios);
            handleCheckAllARL(true, destinatiarios);
          }
        } else {
          if (ativo) {
            setMatriculas([]);
            handleCheckAllARL(false, []);
          }
        }
      } catch (error) {
        snack.error(getError(error));
      } finally {
        if (ativo) setLoading(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [
    turma?.id,
    aluno,
    responsavel,
    loginExtra,
    etapaRecuperacao,
    optantes,
    atribuicao.idMateria,
  ]);

  useEffect(() => {
    if (professores.length > 0) {
      handleCheckAllProfessores(true);
    }
  }, [
    atribuicao.professor,
    atribuicao.idCurso,
    atribuicao.idSerie,
    atribuicao.idTurma,
  ]);

  const checkedARL =
    (atribuicao.aluno &&
      matriculas.length > 0 &&
      (mensagemAtribuicaoAlunos ?? []).length == matriculas.length) ||
    (atribuicao.responsavel &&
      matriculas.length > 0 &&
      (mensagemAtribuicaoResponsaveis ?? []).length == matriculas.length) ||
    (atribuicao.loginExtra &&
      matriculas.length > 0 &&
      (mensagemAtribuicaoLoginsExtras ?? []).length ==
        matriculas
          .filter((f) => f.loginExtras != undefined && f.loginExtras.length > 0)
          .map((f) => f.loginExtras)
          .flatMap((f) => f).length);

  const checkedProfessor =
    atribuicao.professor &&
    professores.length > 0 &&
    (mensagemAtribuicaoProfessores ?? []).length == professores.length;

  const indeterminateARL =
    (atribuicao.aluno &&
      matriculas.length > 0 &&
      (mensagemAtribuicaoAlunos ?? []).length > 0 &&
      (mensagemAtribuicaoAlunos ?? []).length != matriculas.length) ||
    (atribuicao.responsavel &&
      matriculas.length > 0 &&
      (mensagemAtribuicaoResponsaveis ?? []).length > 0 &&
      (mensagemAtribuicaoResponsaveis ?? []).length != matriculas.length) ||
    (atribuicao.loginExtra &&
      matriculas.length > 0 &&
      (mensagemAtribuicaoLoginsExtras ?? []).length > 0 &&
      (mensagemAtribuicaoLoginsExtras ?? []).length !=
        matriculas
          .filter((f) => f.loginExtras != undefined && f.loginExtras.length > 0)
          .map((f) => f.loginExtras)
          .flatMap((f) => f).length);

  const indeterminateProfessor =
    atribuicao.professor &&
    professores.length > 0 &&
    (mensagemAtribuicaoProfessores ?? []).length > 0 &&
    (mensagemAtribuicaoProfessores ?? []).length != professores.length;

  return (
    <>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl component="fieldset" variant="standard">
              <FormLabel component="legend">Tipo destinatário</FormLabel>
              <FormGroup row={!isSm}>
                <FormControlLabel
                  disabled={atribuicao.professor}
                  control={
                    <Checkbox
                      checked={atribuicao.aluno}
                      onChange={() => {
                        setAtribuicao((p) => ({
                          ...p,
                          aluno: !p.aluno,
                        }));
                      }}
                    />
                  }
                  label="Alunos"
                />
                <FormControlLabel
                  disabled={atribuicao.professor}
                  control={
                    <Checkbox
                      checked={atribuicao.responsavel}
                      onChange={() => {
                        setAtribuicao((p) => ({
                          ...p,
                          responsavel: !p.responsavel,
                        }));
                      }}
                    />
                  }
                  label="Responsáveis"
                />
                <FormControlLabel
                  disabled={atribuicao.professor}
                  control={
                    <Checkbox
                      checked={atribuicao.loginExtra}
                      onChange={() => {
                        setAtribuicao((p) => ({
                          ...p,
                          loginExtra: !p.loginExtra,
                        }));
                      }}
                    />
                  }
                  label="Logins extra do aluno"
                />
                {usuario?.tipoUsuario != EUserType.Professor && (
                  <FormControlLabel
                    disabled={
                      atribuicao.aluno ||
                      atribuicao.responsavel ||
                      atribuicao.loginExtra
                    }
                    control={
                      <Checkbox
                        checked={atribuicao.professor}
                        onChange={() => {
                          setAtribuicao((p) => ({
                            ...p,
                            professor: !p.professor,
                          }));
                        }}
                      />
                    }
                    label="Professores"
                  />
                )}
              </FormGroup>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Curso"
              select
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
              value={curso?.id || ""}
              onChange={({ target: { value } }) => {
                const curso = cursos.find((f) => f.id == parseInt(value));
                setAtribuicao((p) => ({
                  ...p,
                  idCurso: !value ? null : parseInt(value, 10),
                  curso: curso ? (curso as ICurso) : undefined,
                  idSerie: null,
                  serie: undefined,
                  idTurma: null,
                  turma: undefined,
                  idMateria: null,
                  mensagemAtribuicaoAlunos: [],
                  mensagemAtribuicaoResponsaveis: [],
                  mensagemAtribuicaoLoginsExtra: [],
                  mensagemAtribuicaoProfessores: [],
                }));
              }}
            >
              {usuario?.tipoUsuario != EUserType.Professor && (
                <MenuItem value="">
                  <b>Todos os cursos</b>
                </MenuItem>
              )}

              {cursos.map((curso) => (
                <MenuItem key={curso.id} value={curso.id}>
                  {curso.nome}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Série"
              select
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
              value={serie?.id || ""}
              onChange={({ target: { value } }) => {
                const serie = series?.find(
                  (f: IFiltroSerie) => f.id == parseInt(value, 10)
                );
                let idMateria: number | null = null;
                let materia: IMateria | undefined = undefined;
                if (serie && usuario?.tipoUsuario == EUserType.Professor) {
                  const materias = utils.getMateriasTreeView({
                    serie,
                    sort: true,
                  });
                  idMateria = materias.at(0)?.id || null;
                  materia = materias.at(0) as IMateria | undefined;
                }
                setAtribuicao((p) => ({
                  ...p,
                  idSerie: !value ? null : parseInt(value, 10),
                  serie: serie ? (serie as ISerie) : undefined,
                  idTurma: null,
                  turma: undefined,
                  idMateria,
                  materia,
                  mensagemAtribuicaoAlunos: [],
                  mensagemAtribuicaoResponsaveis: [],
                  mensagemAtribuicaoLoginsExtra: [],
                  mensagemAtribuicaoProfessores: [],
                }));
              }}
            >
              {usuario?.tipoUsuario != EUserType.Professor && (
                <MenuItem value="">
                  <b>Todas as séries</b>
                </MenuItem>
              )}
              {series?.map((serie: IFiltroSerie) => (
                <MenuItem key={serie.id} value={serie.id}>
                  {serie.descricao}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Turma"
              select
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
              value={turma?.id || ""}
              onChange={({ target: { value } }) => {
                const turma = turmas?.find((f) => f.id == parseInt(value, 10));
                setAtribuicao((p) => ({
                  ...p,
                  idTurma: !value ? null : parseInt(value, 10),
                  turma: turma ? (turma as ITurma) : undefined,
                  mensagemAtribuicaoAlunos: [],
                  mensagemAtribuicaoResponsaveis: [],
                  mensagemAtribuicaoLoginsExtra: [],
                  mensagemAtribuicaoProfessores: [],
                }));
              }}
            >
              {usuario?.tipoUsuario != EUserType.Professor && (
                <MenuItem value="">
                  <b>Todas as turmas</b>
                </MenuItem>
              )}
              {turmas?.map((turma: IFiltroTurma) => (
                <MenuItem key={turma.id} value={turma.id}>
                  {turma.codigoDbf}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Matéria"
              select
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
              value={materia?.id || ""}
              onChange={({ target: { value } }) => {
                const materia = materias.find(
                  (f) => f.id == parseInt(value, 10)
                );
                setAtribuicao((p) => ({
                  ...p,
                  idMateria: !value ? null : parseInt(value, 10),
                  materia: materia ? (materia as IMateria) : undefined,
                }));
                if (!materia) {
                  setOptantes(undefined);
                }
              }}
            >
              {usuario?.tipoUsuario != EUserType.Professor && (
                <MenuItem value="">
                  <b>Independente da matéria</b>
                </MenuItem>
              )}
              {materias?.map((materia: IFiltroMateria) => (
                <MenuItem key={materia.id} value={materia.id}>
                  {materia.nome}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              disabled={atribuicao.professor || optantes != undefined}
              label="Somente em recuperação"
              select
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
              value={etapaRecuperacao || ""}
              onChange={({ target: { value } }) => {
                setEtapaRecuperacao(!value ? undefined : parseInt(value, 10));
              }}
            >
              <MenuItem value="">
                <b>Independente</b>
              </MenuItem>
              {etapas?.map((etapa) => (
                <MenuItem key={etapa.id} value={etapa.etapa}>
                  {sistemaAvaliacao
                    ? `no ${etapa.etapa}º ${sistemaAvaliacao.nomeEtapa}`
                    : `na ${etapa.etapa}ª etapa`}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              disabled={
                atribuicao.professor ||
                !atribuicao.idMateria ||
                etapaRecuperacao != undefined
              }
              label="Optantes"
              select
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
              value={optantes || ""}
              onChange={({ target: { value } }) => {
                setOptantes(!value ? undefined : parseInt(value, 10));
              }}
            >
              <MenuItem value="">
                <b>Todos os alunos</b>
              </MenuItem>
              {etapas?.map((etapa) => (
                <MenuItem key={etapa.id} value={etapa.etapa}>
                  {sistemaAvaliacao
                    ? `no ${etapa.etapa}º ${sistemaAvaliacao.nomeEtapa}`
                    : `na ${etapa.etapa}ª etapa`}
                </MenuItem>
              ))}
              <MenuItem value="5">Independente da etapa</MenuItem>
            </TextField>
          </Grid>
          {loading && (
            <Grid item xs={12}>
              <LinearProgress />
            </Grid>
          )}
          {!loading && matriculas.length > 0 && (
            <Grid item xs={12}>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ maxHeight: 300 }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={checkedARL}
                          indeterminate={indeterminateARL}
                          onChange={() => {
                            handleCheckAllARL(!checkedARL, matriculas);
                          }}
                        />
                      </TableCell>
                      {atribuicao.aluno && <TableCell>Aluno</TableCell>}
                      {atribuicao.responsavel && (
                        <TableCell>Responsável</TableCell>
                      )}
                      {atribuicao.loginExtra && (
                        <TableCell>Login extra</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {matriculas.map((matAluno, index) => {
                      const checked =
                        (atribuicao.aluno &&
                          mensagemAtribuicaoAlunos != undefined &&
                          mensagemAtribuicaoAlunos.find(
                            (f) => f.idAluno == matAluno.aluno.uid
                          ) != undefined) ||
                        (atribuicao.responsavel &&
                          mensagemAtribuicaoResponsaveis != undefined &&
                          mensagemAtribuicaoResponsaveis.find(
                            (f) => f.idResponsavel == matAluno.responsavel?.id
                          ) != undefined) ||
                        (atribuicao.loginExtra &&
                          mensagemAtribuicaoLoginsExtras != undefined &&
                          mensagemAtribuicaoLoginsExtras.find((f) =>
                            (matAluno.loginExtras ?? [])
                              .map((m) => m.id)
                              .includes(f.idLoginExtra)
                          ) != undefined);

                      const nomeAluno = (
                        matAluno.aluno.nomeusual?.trim() || matAluno.aluno.nome
                      )?.rUpper();
                      return (
                        <TableRow
                          hover
                          role="checkbox"
                          selected={checked}
                          key={index}
                          sx={{ cursor: "pointer" }}
                          onClick={() =>
                            handleChangeAtribuicaoARL(matAluno, checked)
                          }
                        >
                          <TableCell padding="checkbox">
                            <Checkbox checked={checked} />
                          </TableCell>
                          {atribuicao.aluno && (
                            <TableCell>{`${matAluno.aluno.codigo} - ${matAluno.matricula.nrChamada} - ${nomeAluno}`}</TableCell>
                          )}
                          {atribuicao.responsavel && (
                            <TableCell>
                              {`
                                ${matAluno.responsavel?.codigo}
                                -
                                ${matAluno.responsavel?.nome?.rUpper()}
                                ${!atribuicao.aluno ? `(${nomeAluno})` : ""}
                              `}
                            </TableCell>
                          )}
                          {atribuicao.loginExtra && (
                            <TableCell>
                              {(matAluno.loginExtras ?? [])
                                .map((item) =>
                                  item.nome
                                    ? item.nome.concat(item.tipo)
                                    : item.tipo
                                )
                                .joinEOu()}
                            </TableCell>
                          )}
                          {atribuicao.professor && <TableCell></TableCell>}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}
          {!loading && professores.length > 0 && (
            <Grid item xs={12}>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ maxHeight: 300 }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={checkedProfessor}
                          indeterminate={indeterminateProfessor}
                          onChange={() => {
                            handleCheckAllProfessores(!checkedProfessor);
                          }}
                        />
                      </TableCell>
                      <TableCell>Professor</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {professores.map((professor, index) => {
                      const checked =
                        (mensagemAtribuicaoProfessores ?? []).find(
                          (f) => f.idProfessor == professor.id
                        ) != undefined;
                      return (
                        <TableRow
                          hover
                          role="checkbox"
                          selected={checked}
                          key={index}
                          sx={{ cursor: "pointer" }}
                          onClick={() =>
                            handleChangeAtribuicaoProfessor(professor, checked)
                          }
                        >
                          <TableCell padding="checkbox">
                            <Checkbox checked={checked} />
                          </TableCell>
                          <TableCell>
                            {`${
                              professor.codigo
                            } - ${professor.nome?.rUpper()}`}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Voltar
        </Button>
        <Button
          disabled={
            !atribuicao.aluno &&
            !atribuicao.responsavel &&
            !atribuicao.loginExtra &&
            !atribuicao.professor
          }
          onClick={() => handleAdd(true)}
        >
          Adicionar e Fechar
        </Button>
        <Button
          disabled={
            !atribuicao.aluno &&
            !atribuicao.responsavel &&
            !atribuicao.loginExtra &&
            !atribuicao.professor
          }
          onClick={() => handleAdd()}
        >
          Adicionar
        </Button>
      </DialogActions>
    </>
  );
};
